// Closing-count split: which of the counted bills and coins stay in the drawer
// as tomorrow's opening bank, and which go into the sealed deposit bag.
//
// The edge function (Library: cash-drawer-count) rejects a closing count unless
// `retained + shortfall === standard bank` and every denomination is conserved
// (retained + deposit === counted). So the retained set must sum to the bank
// EXACTLY whenever the counted denominations allow it; only when no combination
// reaches the bank may we retain less and declare the remainder as a shortfall.
// Retaining more than the bank is never allowed — the excess is a deposit.
//
// The previous greedy fill ("smallest denominations first, then borrow one
// larger bill to cover the remainder") could overshoot the bank — e.g. a drawer
// opened at $300.20 in coins/small bills retained $300.20, the server refused
// the count, and the deposit shown to the closer was short by the overshoot.

export type DenomCounts = Record<string, number>;

export interface ClosingSplit {
  retained: DenomCounts;
  retainedTotal: number;
  deposit: DenomCounts;
  depositTotal: number;
  /** Amount the drawer falls short of the standard bank (0 when the bank is met). */
  shortfall: number;
}

/** US denominations in cents, largest first. Must match VALID_DENOMS in cash-drawer-count. */
export const CASH_DENOMS_CENTS = [10000, 5000, 2000, 1000, 500, 100, 25, 10, 5, 1] as const;

// The float should avoid large bills (they trigger the morning change swap),
// so the bank is first attempted with nothing above a $20, then allowing $50s,
// then allowing $100s. The first tier that can make the bank exactly wins.
const LARGE_BILL_TIERS = [2000, 5000, 10000] as const;

interface DenomQty {
  cents: number;
  qty: number;
}

function normalizeCounts(counted: DenomCounts | null | undefined): DenomQty[] {
  const out: DenomQty[] = [];
  const src = counted && typeof counted === "object" ? counted : {};
  for (const cents of CASH_DENOMS_CENTS) {
    const raw = src[String(cents)];
    const qty = typeof raw === "number" && Number.isFinite(raw) ? Math.floor(raw) : 0;
    if (qty > 0) out.push({ cents, qty });
  }
  // ascending — smallest change is preferred for the drawer float
  return out.sort((a, b) => a.cents - b.cents);
}

export function totalCents(counted: DenomCounts | null | undefined): number {
  return normalizeCounts(counted).reduce((sum, d) => sum + d.cents * d.qty, 0);
}

/**
 * Bounded-knapsack reachability. reach[i][a] === 1 when amount `a` can be made
 * exactly from denominations i..n-1 (ascending order) within their counted
 * quantities. reach[n] is the empty suffix: only 0 is reachable.
 */
function reachability(denoms: DenomQty[], bank: number): Uint8Array[] {
  const n = denoms.length;
  const reach: Uint8Array[] = new Array(n + 1);
  reach[n] = new Uint8Array(bank + 1);
  reach[n][0] = 1;
  for (let i = n - 1; i >= 0; i--) {
    const { cents, qty } = denoms[i];
    const next = reach[i + 1];
    const cur = new Uint8Array(bank + 1);
    const used = new Uint32Array(bank + 1); // how many of denom i were used to reach `a`
    for (let a = 0; a <= bank; a++) {
      if (next[a]) {
        cur[a] = 1;
        used[a] = 0;
      } else if (a >= cents && cur[a - cents] && used[a - cents] < qty) {
        cur[a] = 1;
        used[a] = used[a - cents] + 1;
      }
    }
    reach[i] = cur;
  }
  return reach;
}

/**
 * Split a counted closing drawer into the retained opening bank and the deposit.
 *
 * Keeps the smallest denominations in the drawer (change for tomorrow) while
 * hitting the standard bank exactly, and keeps a $50 or $100 only when nothing
 * smaller can make the bank. When the counted mix cannot make the bank exactly
 * at all, retains the largest reachable amount below it and reports the
 * difference as `shortfall`.
 */
export function computeClosingSplit(
  counted: DenomCounts | null | undefined,
  standardBankCents: number
): ClosingSplit {
  const all = normalizeCounts(counted);
  const total = all.reduce((sum, d) => sum + d.cents * d.qty, 0);
  const bankRaw = Number(standardBankCents);
  const bank = Number.isFinite(bankRaw) && bankRaw > 0 ? Math.floor(bankRaw) : 0;

  const retained: DenomCounts = {};
  const deposit: DenomCounts = {};

  // 1. Drawer holds no more than the bank: everything stays, nothing to deposit.
  if (total <= bank) {
    for (const d of all) retained[String(d.cents)] = d.qty;
    return { retained, retainedTotal: total, deposit, depositTotal: 0, shortfall: bank - total };
  }

  // 2. Find the smallest tier of large bills that can make the bank exactly.
  let pool: DenomQty[] | null = null;
  let reach: Uint8Array[] | null = null;
  let target = bank;
  for (const maxCents of LARGE_BILL_TIERS) {
    const subset = all.filter((d) => d.cents <= maxCents);
    const r = reachability(subset, bank);
    if (r[0][bank]) {
      pool = subset;
      reach = r;
      break;
    }
  }

  // 3. No mix makes the bank exactly: retain the largest reachable amount below
  //    it, drawing on every denomination, and report the rest as a shortfall.
  if (!pool || !reach) {
    pool = all;
    reach = reachability(all, bank);
    while (target > 0 && !reach[0][target]) target--;
  }

  // 4. Greedy from the smallest denomination up: keep as many as possible of
  //    each while the remainder stays reachable with the larger ones.
  let remaining = target;
  let retainedTotal = 0;
  const kept = new Map<number, number>();
  for (let i = 0; i < pool.length; i++) {
    const { cents, qty } = pool[i];
    let keep = Math.min(qty, Math.floor(remaining / cents));
    while (keep > 0 && !reach[i + 1][remaining - keep * cents]) keep--;
    if (keep > 0) {
      kept.set(cents, keep);
      retainedTotal += keep * cents;
      remaining -= keep * cents;
    }
  }

  // 5. Everything not kept — including any large bills outside the chosen
  //    tier — goes to the deposit bag.
  for (const d of all) {
    const key = String(d.cents);
    const keep = kept.get(d.cents) || 0;
    if (keep > 0) retained[key] = keep;
    if (d.qty - keep > 0) deposit[key] = d.qty - keep;
  }

  return {
    retained,
    retainedTotal,
    deposit,
    depositTotal: total - retainedTotal,
    shortfall: bank - retainedTotal,
  };
}
